import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AIAssistButton from "../components/AIAssistButton";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ShieldAlert, AlertOctagon, Cpu, Target, FileCheck,
  FileText, Megaphone, ClipboardCheck, Server, Files, Users, GraduationCap,
  Inbox, ArrowUpRight, Trash2, CheckCircle2, Rocket, Loader2
} from "lucide-react";

/**
 * AI Lab — one hub page exposing every Wave-B AI tool.
 * Each tool is a card with inputs + an <AIAssistButton/> that calls the endpoint.
 */
export default function AILabPage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  // AI Drafts Inbox
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [promotingId, setPromotingId] = useState(null);

  const loadDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const res = await api.get("/ai-copilot/drafts");
      setDrafts(res.data?.drafts || []);
    } catch (e) {
      // silent
    } finally {
      setDraftsLoading(false);
    }
  }, [api]);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const promoteDraft = async (d) => {
    setPromotingId(d.id);
    try {
      const res = await api.post(`/ai-copilot/drafts/${d.id}/promote`);
      if (res.data?.success) {
        toast.success(`${res.data.message} · ${res.data.count || 1} live record(s)`);
        loadDrafts();
      } else {
        toast.error(res.data?.error || "Promote failed");
      }
    } catch (e) {
      toast.error("Promote failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPromotingId(null);
    }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/ai-copilot/drafts/${id}`);
      toast.success("Draft deleted");
      loadDrafts();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const TOOL_META = {
    risk:       { icon: ShieldAlert,      color: "text-red-600" },
    threat:     { icon: AlertOctagon,     color: "text-amber-600" },
    asset:      { icon: Cpu,              color: "text-sky-600" },
    exercise:   { icon: Target,           color: "text-emerald-600" },
    audit:      { icon: FileCheck,        color: "text-indigo-600" },
    policy:     { icon: FileText,         color: "text-slate-700" },
    crisis:     { icon: Megaphone,        color: "text-rose-600" },
    mgmtrev:    { icon: ClipboardCheck,   color: "text-purple-600" },
    infradrift: { icon: Server,           color: "text-teal-600" },
    doc:        { icon: Files,            color: "text-orange-600" },
    raci:       { icon: Users,            color: "text-cyan-600" },
    quiz:       { icon: GraduationCap,    color: "text-pink-600" },
  };

  // Shared state per tool (kept in one object for compactness)
  const [s, setS] = useState({
    risk: { risk_title: "Ransomware encrypting PAS database", category: "cyber", likelihood: "high", impact: "critical" },
    threat: { location: "Chicago, US", industry: "Insurance" },
    asset: { asset_name: "Policy Administration System", asset_type: "Application", linked_process_tiers: [1, 1, 2] },
    exercise: { exercise_type: "tabletop", target_scenario: "Ransomware attack on PAS", duration_minutes: 90 },
    audit: { iso_clause: "8.4", audit_scope: "IT-DRP program" },
    policy: { organization_name: "Meridian Insurance Group", industry: "Insurance", scope_summary: "Enterprise-wide BCM covering underwriting, claims, and IT operations" },
    crisis: { scenario: "Ransomware attack — PAS offline for 6 hours", severity: "critical" },
    mgmtrev: { period_label: "Q1 2026" },
    doc: { document_title: "AWS DR SLA", document_type: "sla", document_text: "AWS commits to 99.95% uptime for EC2 in us-east-1 with quarterly credit-back if SLA is missed. Customer must notify within 30 days of incident. Maximum credit is 30% of monthly fee. Critical workloads must use multi-AZ deployment." },
    raci: { activity: "Annual BCM Policy review and approval", context: "Insurance sector, ISO 22301 Clause 5.2", available_roles: "CEO, CISO, BCM Manager, Legal Counsel, Board" },
    quiz: { topic: "BCM Policy v2.1", target_role: "all-staff", question_count: 8, difficulty: "medium" },
  });
  const set = (k, patch) => setS(prev => ({ ...prev, [k]: { ...prev[k], ...patch } }));

  return (
    <div className="space-y-6 p-6" data-testid="ai-lab-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-purple-600" />
            AI Lab
          </h1>
          <p className="text-slate-600 mt-1">
            Explore every AI copilot on one screen — Risk, Threats, Assets, Exercises, Audits, Policy, Crisis Comms,
            Management Review, Infrastructure Drift, Documents, RACI, and Training.
          </p>
        </div>
        <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700 px-3 py-1.5">
          <Sparkles className="w-3 h-3 mr-1" /> Powered by AI · 12 copilots live
        </Badge>
      </div>

      {/* AI Drafts Inbox */}
      <Card data-testid="ai-drafts-inbox" className="border-purple-200 bg-gradient-to-r from-purple-50 via-white to-sky-50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Inbox className="w-5 h-5 text-purple-600" />
              AI Drafts Inbox
              <Badge variant="outline" className="ml-1">{drafts.filter(d => d.status !== "promoted").length} pending</Badge>
              {drafts.some(d => d.status === "promoted") && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  {drafts.filter(d => d.status === "promoted").length} promoted
                </Badge>
              )}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={loadDrafts} disabled={draftsLoading}
              data-testid="refresh-drafts-btn">
              {draftsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
            </Button>
          </div>
          <CardDescription>
            AI outputs you saved from the copilots below. Click <strong>Promote</strong> to create
            real records in the matching module (Risks, Threats, Crisis Comms, etc.).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {drafts.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              No AI drafts yet. Run any copilot below and click <strong>Save to Workspace</strong> to queue one here.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {drafts.map(d => {
                const meta = TOOL_META[d.tool_key] || { icon: Sparkles, color: "text-purple-600" };
                const Icon = meta.icon;
                const isPromoted = d.status === "promoted";
                return (
                  <div key={d.id}
                    data-testid={`draft-card-${d.tool_key}`}
                    className={`rounded-lg border p-3 bg-white ${isPromoted ? "border-emerald-200 opacity-80" : "border-slate-200"}`}>
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{d.title}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-slate-500">
                          <Badge variant="outline" className="text-[9px] uppercase">{d.tool_key}</Badge>
                          <span>·</span>
                          <span>{new Date(d.created_at).toLocaleDateString()}</span>
                          {isPromoted && (
                            <>
                              <span>·</span>
                              <span className="text-emerald-700 font-medium flex items-center gap-0.5">
                                <CheckCircle2 className="w-3 h-3" /> Promoted
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 mt-2.5 flex-wrap">
                      {!isPromoted && (
                        <Button
                          size="sm" variant="default"
                          className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 gap-1 text-xs"
                          onClick={() => promoteDraft(d)}
                          disabled={promotingId === d.id}
                          data-testid={`promote-draft-${d.tool_key}`}
                        >
                          {promotingId === d.id
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Promoting…</>
                            : <><Rocket className="w-3 h-3" /> Promote</>}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs"
                        onClick={() => navigate(d.target_route || "/dashboard")}>
                        <ArrowUpRight className="w-3 h-3" /> Open Module
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-xs text-slate-500"
                        onClick={() => deleteDraft(d.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">

        {/* #2 Risk Mitigation */}
        <Card data-testid="tool-risk">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-600" /> Risk Mitigation</CardTitle>
            <CardDescription>Suggest controls for an identified risk.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Risk title" value={s.risk.risk_title} onChange={e => set("risk", { risk_title: e.target.value })} />
            <Input placeholder="Category" value={s.risk.category} onChange={e => set("risk", { category: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Select value={s.risk.likelihood} onValueChange={v => set("risk", { likelihood: v })}>
                <SelectTrigger><SelectValue placeholder="Likelihood" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Select value={s.risk.impact} onValueChange={v => set("risk", { impact: v })}>
                <SelectTrigger><SelectValue placeholder="Impact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <AIAssistButton endpoint="/ai-copilot/risk/suggest-mitigation" toolKey="risk" payload={() => s.risk}
              label="Suggest Controls" title="AI Risk Mitigation" testId="ai-risk-btn" />
          </CardContent>
        </Card>

        {/* #5 Threat Profiling */}
        <Card data-testid="tool-threat">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-amber-600" /> Threat Profiling</CardTitle>
            <CardDescription>Rank threats for a location + industry.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Location" value={s.threat.location} onChange={e => set("threat", { location: e.target.value })} />
            <Input placeholder="Industry" value={s.threat.industry} onChange={e => set("threat", { industry: e.target.value })} />
            <AIAssistButton endpoint="/ai-copilot/threat/suggest-threats" toolKey="threat" payload={() => s.threat}
              label="Suggest Threats" title="AI Threat Profiling" testId="ai-threat-btn" />
          </CardContent>
        </Card>

        {/* #6 Asset Criticality */}
        <Card data-testid="tool-asset">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Cpu className="w-4 h-4 text-sky-600" /> Asset Criticality</CardTitle>
            <CardDescription>Explain derived criticality tier.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Asset name" value={s.asset.asset_name} onChange={e => set("asset", { asset_name: e.target.value })} />
            <Input placeholder="Asset type" value={s.asset.asset_type} onChange={e => set("asset", { asset_type: e.target.value })} />
            <Input placeholder="Linked tiers (e.g. 1,1,2)"
              value={(s.asset.linked_process_tiers || []).join(",")}
              onChange={e => set("asset", { linked_process_tiers: e.target.value.split(",").map(v => parseInt(v.trim(), 10) || 0).filter(Boolean) })} />
            <AIAssistButton endpoint="/ai-copilot/asset/explain-criticality" toolKey="asset" payload={() => s.asset}
              label="Explain Criticality" title="AI Asset Criticality" testId="ai-asset-btn" />
          </CardContent>
        </Card>

        {/* #7 Exercise Design */}
        <Card data-testid="tool-exercise">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-emerald-600" /> Exercise Design</CardTitle>
            <CardDescription>Auto-draft injects + rubric.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Select value={s.exercise.exercise_type} onValueChange={v => set("exercise", { exercise_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tabletop">Tabletop</SelectItem>
                <SelectItem value="functional">Functional</SelectItem>
                <SelectItem value="full-scale">Full-scale</SelectItem>
                <SelectItem value="walkthrough">Walkthrough</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Target scenario" value={s.exercise.target_scenario} onChange={e => set("exercise", { target_scenario: e.target.value })} />
            <Input type="number" placeholder="Duration (min)" value={s.exercise.duration_minutes || 90}
              onChange={e => set("exercise", { duration_minutes: parseInt(e.target.value, 10) || 90 })} />
            <AIAssistButton endpoint="/ai-copilot/exercise/design-scenario" toolKey="exercise" payload={() => s.exercise}
              label="Design Exercise" title="AI Exercise Designer" testId="ai-exercise-btn" />
          </CardContent>
        </Card>

        {/* #8 Audit Questions */}
        <Card data-testid="tool-audit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileCheck className="w-4 h-4 text-indigo-600" /> Audit Questions</CardTitle>
            <CardDescription>Generate ISO-clause audit checklist.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="ISO Clause (e.g. 8.4)" value={s.audit.iso_clause} onChange={e => set("audit", { iso_clause: e.target.value })} />
            <Input placeholder="Scope" value={s.audit.audit_scope} onChange={e => set("audit", { audit_scope: e.target.value })} />
            <AIAssistButton endpoint="/ai-copilot/audit/generate-questions" toolKey="audit" payload={() => s.audit}
              label="Generate Questions" title="AI Audit Question Generator" testId="ai-audit-btn" />
          </CardContent>
        </Card>

        {/* #9 Policy Drafter */}
        <Card data-testid="tool-policy">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-slate-700" /> BCM Policy Drafter</CardTitle>
            <CardDescription>Draft full ISO 22301 policy.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Organization name" value={s.policy.organization_name} onChange={e => set("policy", { organization_name: e.target.value })} />
            <Input placeholder="Industry" value={s.policy.industry} onChange={e => set("policy", { industry: e.target.value })} />
            <Textarea rows={2} placeholder="Scope summary" value={s.policy.scope_summary} onChange={e => set("policy", { scope_summary: e.target.value })} />
            <AIAssistButton endpoint="/ai-copilot/policy/draft" toolKey="policy" payload={() => s.policy}
              label="Draft Policy" title="AI BCM Policy Drafter" testId="ai-policy-btn" />
          </CardContent>
        </Card>

        {/* #10 Crisis Statement */}
        <Card data-testid="tool-crisis">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4 text-rose-600" /> Crisis Comms</CardTitle>
            <CardDescription>Draft statements for 4 audiences.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Scenario" value={s.crisis.scenario} onChange={e => set("crisis", { scenario: e.target.value })} />
            <Select value={s.crisis.severity} onValueChange={v => set("crisis", { severity: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <AIAssistButton endpoint="/ai-copilot/crisis/draft-statement" toolKey="crisis" payload={() => s.crisis}
              label="Draft Statements" title="AI Crisis Comms" testId="ai-crisis-btn" />
          </CardContent>
        </Card>

        {/* #11 Management Review */}
        <Card data-testid="tool-mgmtrev">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-purple-600" /> Management Review Pack</CardTitle>
            <CardDescription>Auto-generate 9.3 review pack.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Period (e.g. Q1 2026)" value={s.mgmtrev.period_label} onChange={e => set("mgmtrev", { period_label: e.target.value })} />
            <AIAssistButton endpoint="/ai-copilot/management-review/auto-pack" toolKey="mgmtrev" payload={() => s.mgmtrev}
              label="Generate Pack" title="AI Management Review Pack" testId="ai-mgmtrev-btn" />
          </CardContent>
        </Card>

        {/* #12 Infrastructure Drift */}
        <Card data-testid="tool-infradrift">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4 text-teal-600" /> Infrastructure Drift</CardTitle>
            <CardDescription>Scan DR pairing + EOL OS.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-xs text-slate-500">
            Scans all 32+ infra items across Application Profiles.
            <AIAssistButton endpoint="/ai-copilot/infrastructure/detect-drift" toolKey="infradrift" payload={{}}
              label="Detect Drift" title="AI Infrastructure Drift Detector" testId="ai-infradrift-btn" />
          </CardContent>
        </Card>

        {/* #13 Document Summarizer */}
        <Card data-testid="tool-doc">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Files className="w-4 h-4 text-orange-600" /> Document Summarizer</CardTitle>
            <CardDescription>Extract commitments + SLAs.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Document title" value={s.doc.document_title} onChange={e => set("doc", { document_title: e.target.value })} />
            <Select value={s.doc.document_type} onValueChange={v => set("doc", { document_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="sla">SLA</SelectItem>
                <SelectItem value="runbook">Runbook</SelectItem>
                <SelectItem value="policy">Policy</SelectItem>
                <SelectItem value="vendor-dr-plan">Vendor DR Plan</SelectItem>
              </SelectContent>
            </Select>
            <Textarea rows={3} placeholder="Paste document text" value={s.doc.document_text} onChange={e => set("doc", { document_text: e.target.value })} />
            <AIAssistButton endpoint="/ai-copilot/document/summarize" toolKey="doc" payload={() => s.doc}
              label="Summarize" title="AI Document Summarizer" testId="ai-doc-btn" />
          </CardContent>
        </Card>

        {/* #14 RACI Suggester */}
        <Card data-testid="tool-raci">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-cyan-600" /> RACI Suggester</CardTitle>
            <CardDescription>Assign R/A/C/I to an activity.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Activity" value={s.raci.activity} onChange={e => set("raci", { activity: e.target.value })} />
            <Input placeholder="Context" value={s.raci.context} onChange={e => set("raci", { context: e.target.value })} />
            <Input placeholder="Available roles (comma-sep)" value={s.raci.available_roles} onChange={e => set("raci", { available_roles: e.target.value })} />
            <AIAssistButton endpoint="/ai-copilot/stakeholder/suggest-raci"
              toolKey="raci"
              payload={() => ({ activity: s.raci.activity, context: s.raci.context, available_roles: s.raci.available_roles.split(",").map(r => r.trim()).filter(Boolean) })}
              label="Suggest RACI" title="AI RACI Suggester" testId="ai-raci-btn" />
          </CardContent>
        </Card>

        {/* #15 Training Quiz */}
        <Card data-testid="tool-quiz">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="w-4 h-4 text-pink-600" /> Training Quiz</CardTitle>
            <CardDescription>Generate role-specific quiz.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Topic" value={s.quiz.topic} onChange={e => set("quiz", { topic: e.target.value })} />
            <Input placeholder="Target role" value={s.quiz.target_role} onChange={e => set("quiz", { target_role: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Questions" value={s.quiz.question_count} onChange={e => set("quiz", { question_count: parseInt(e.target.value, 10) || 8 })} />
              <Select value={s.quiz.difficulty} onValueChange={v => set("quiz", { difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <AIAssistButton endpoint="/ai-copilot/training/generate-quiz" toolKey="quiz" payload={() => s.quiz}
              label="Generate Quiz" title="AI Training Quiz Generator" testId="ai-quiz-btn" />
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
